import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { RentalListing, RentalSearch } from '../types';
import rentalApi from '../services/api';
import { useFocusTrap } from '../hooks/useFocusTrap';
import './PinPopup.css';

type Entity =
  | { kind: 'listing'; data: RentalListing; deleteToken: string }
  | { kind: 'search';  data: RentalSearch;  deleteToken: string };

interface Props {
  entity: Entity;
  onClose: () => void;
  onDeleted?: () => void;
}

const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

type Tag = { emoji: string; text: string };

function buildListingTags(d: RentalListing): Tag[] {
  const tags: Tag[] = [];
  d.property_type?.split(',').forEach(pt =>          tags.push({ emoji: '🏠', text: pt.trim() }));
  if (d.bhk != null)                                 tags.push({ emoji: '🛏', text: `${d.bhk} Bed` });
  if (d.baths)                                       tags.push({ emoji: '🚿', text: `${d.baths} Bath` });
  if (d.furnishing)                                  tags.push({ emoji: '🪑', text: d.furnishing });
  if (d.availability)                                tags.push({ emoji: '📅', text: d.availability });
  if (d.pets_allowed_status)                         tags.push({ emoji: '🐾', text: `Pets: ${d.pets_allowed_status}` });
  if (d.transit_distance)                            tags.push({ emoji: '🚌', text: d.transit_distance });
  d.utilities_included?.forEach(u =>                 tags.push({ emoji: '💡', text: u }));
  d.amenities?.forEach(a =>                          tags.push({ emoji: '✨', text: a }));
  if (d.school_rating)                               tags.push({ emoji: '🏫', text: `School ${d.school_rating}/10` });
  if (d.desired_tenant_gender && d.desired_tenant_gender !== 'Any')
                                                     tags.push({ emoji: '👤', text: `Tenant: ${d.desired_tenant_gender}` });
  return tags;
}

function buildSearchTags(d: RentalSearch): Tag[] {
  const tags: Tag[] = [];
  if (d.move_in_timeline)                            tags.push({ emoji: '📅', text: d.move_in_timeline });
  if (d.bhk_preference != null)                      tags.push({ emoji: '🛏', text: `${d.bhk_preference} Bed` });
  d.property_type?.split(',').forEach(pt =>          tags.push({ emoji: '🏠', text: pt.trim() }));
  if (d.furnishing)                                  tags.push({ emoji: '🪑', text: d.furnishing });
  if (d.pets_allowed_status)                         tags.push({ emoji: '🐾', text: `Pets: ${d.pets_allowed_status}` });
  if (d.transit_distance)                            tags.push({ emoji: '🚌', text: d.transit_distance });
  d.utilities_included?.forEach(u =>                 tags.push({ emoji: '💡', text: u }));
  d.amenities?.forEach(a =>                          tags.push({ emoji: '✨', text: a }));
  if (d.school_rating)                               tags.push({ emoji: '🏫', text: `School ${d.school_rating}/10` });
  if (d.food_preference)                             tags.push({ emoji: '🍽', text: d.food_preference });
  if (d.desired_flatmate_gender && d.desired_flatmate_gender !== 'Any')
                                                     tags.push({ emoji: '👤', text: `With: ${d.desired_flatmate_gender}` });
  return tags;
}

export const DeleteListingPopup: React.FC<Props> = ({ entity, onClose, onDeleted }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState('');
  const trapRef = useFocusTrap(true);

  const { kind, data, deleteToken } = entity;

  const label = kind === 'listing'
    ? ((data as RentalListing).listing_type === 'RentingRoom' ? 'Room Listing' : 'Property Listing')
    : ((data as RentalSearch).search_type  === 'LookingForRoom' ? 'Room Search' : 'Property Search');

  const budget = (data as RentalListing | RentalSearch).budget;

  const location = kind === 'listing'
    ? (() => { const d = data as RentalListing; return d.address ? `${d.address}, ${d.city}` : d.city; })()
    : (data as RentalSearch).city;

  const tags = kind === 'listing'
    ? buildListingTags(data as RentalListing)
    : buildSearchTags(data as RentalSearch);

  const description = (data as { description?: string }).description;

  const postedDate = new Date(data.created_at).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      if (kind === 'listing') {
        await rentalApi.deleteRentalListing(data.id, deleteToken);
      } else {
        await rentalApi.deleteRentalSearch(data.id, deleteToken);
      }
      onClose();
      onDeleted?.();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete. Try again.');
      setDeleting(false);
    }
  };

  const popup = (
    <div className="pp-backdrop" onClick={onClose} aria-hidden="true">
      <div
        className="pp-card"
        ref={el => { trapRef.current = el; }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dlp-title"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="pp-header">
          <div className="pp-rent">
            <span id="dlp-title" className="pp-rent-amount">${budget?.toLocaleString()}<span className="pp-rent-period">/mo</span></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="pp-action-btn pp-action-delete"
              onClick={() => setConfirmDelete(true)}
              aria-label="Delete your listing"
            >
              <TrashIcon />
            </button>
            <button className="pp-action-btn pp-action-close" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Owner badge + type label */}
        <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="pp-owner-badge" style={{ margin: 0 }}><span aria-hidden="true">✓</span> Your {kind === 'listing' ? 'listing' : 'search'}</div>
          <span className="pp-badge pp-badge-type">{label}</span>
        </div>

        {/* Address / city */}
        <div className="pp-address"><span aria-hidden="true">📍</span> {location}</div>

        {/* Detail tags */}
        {tags.length > 0 && (
          <ul className="pp-tags" aria-label="Listing details">
            {tags.map((tag, i) => (
              <li key={i} className="pp-tag">
                <span className="pp-tag-emoji" aria-hidden="true">{tag.emoji}</span>
                <span>{tag.text}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Description */}
        {description && <p className="pp-description">{description}</p>}

        {/* Delete error */}
        {deleteError && (
          <div style={{ padding: '0 16px' }}>
            <div className="pp-error" role="alert">{deleteError}</div>
          </div>
        )}

        {/* Confirm delete */}
        {confirmDelete && (
          <div style={{ padding: '0 16px 16px' }} role="alertdialog" aria-label="Confirm deletion">
            <div className="pp-confirm-delete">
              <p>Remove this {kind === 'listing' ? 'listing' : 'search'}?</p>
              <div className="pp-confirm-actions">
                <button
                  className="pp-btn pp-btn-cancel"
                  onClick={() => { setConfirmDelete(false); setDeleteError(''); }}
                >
                  Cancel
                </button>
                <button className="pp-btn pp-btn-delete" onClick={handleDelete} disabled={deleting} aria-busy={deleting}>
                  {deleting ? 'Deleting…' : 'Yes, remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pp-footer">
          <div className="pp-date"><span aria-hidden="true">📅</span> Posted {postedDate}</div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(popup, document.body);
};
